import React, { useState, useContext } from "react"
import i18n from "../../../../services/i18n"
import {
  Card,
  Button,
  Space,
  Typography,
  Divider,
  Alert,
  Modal,
  Input,
  message,
  Descriptions,
  Spin,
  Tag,
} from "antd"
import { Database, Trash2, RotateCcw, AlertTriangle, CheckCircle, RefreshCw, User, Lock } from "react-feather"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { request } from "../../../../utils/config"
import { AuthContext } from "../../../../contexts/auth/AuthContext"

export default function DatabaseResetTab() {
  const queryClient = useQueryClient()
  const { authState } = useContext(AuthContext)
  const currentUsername = (authState?.user as any)?.userName || (authState?.user as any)?.username || localStorage.getItem("username") || "Administrator"

  const [modalType, setModalType] = useState<"clearTest" | "fullReset" | null>(null)
  const [adminLogin, setAdminLogin] = useState<string>(currentUsername)
  const [adminPassword, setAdminPassword] = useState<string>("")
  const [confirmInput, setConfirmInput] = useState<string>("")

  // Fetch Database Summary
  const {
    data: summary,
    isLoading: loadingSummary,
    refetch,
  } = useQuery(
    ["GET_DATABASE_SUMMARY"],
    async () => {
      const res = await request({
        url: "Helpers/DatabaseSummary",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      })
      return res.data
    },
    { refetchOnWindowFocus: false }
  )

  // Clear Test Data Mutation
  const clearTestDataMutation = useMutation(
    async (payload: { login: string; password: string }) => {
      return await request({
        url: "Helpers/ClearTestData",
        method: "POST",
        data: payload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "X-Confirm-Dangerous": "YES",
        },
      })
    },
    {
      onSuccess: () => {
        message.success(i18n.t("msgTestDataCleared"))
        setModalType(null)
        setAdminPassword("")
        setConfirmInput("")
        queryClient.invalidateQueries()
        refetch()
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.message || err?.response?.data?.Message || "Tozalashda xatolik yuz berdi")
      },
    }
  )

  // Full Database Reset Mutation
  const fullResetMutation = useMutation(
    async (payload: { login: string; password: string }) => {
      return await request({
        url: "Helpers/ResetDatabase",
        method: "POST",
        data: payload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "X-Confirm-Dangerous": "YES",
        },
      })
    },
    {
      onSuccess: () => {
        message.success(i18n.t("msgDbReset"))
        setModalType(null)
        setAdminPassword("")
        setConfirmInput("")
        queryClient.invalidateQueries()
        refetch()
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.message || err?.response?.data?.Message || "Qaytarishda xatolik yuz berdi")
      },
    }
  )

  const handleExecute = () => {
    if (!adminLogin.trim()) {
      message.error(i18n.t("msgEnterSuperAdminLogin"))
      return
    }

    if (!adminPassword.trim()) {
      message.error(i18n.t("msgEnterSuperAdminPassword"))
      return
    }

    if (confirmInput.trim().toUpperCase() !== "TOZALASH") {
      message.error(i18n.t("msgConfirmWordWrong"))
      return
    }

    const payload = {
      login: adminLogin.trim(),
      password: adminPassword,
    }

    if (modalType === "clearTest") {
      clearTestDataMutation.mutate(payload)
    } else if (modalType === "fullReset") {
      fullResetMutation.mutate(payload)
    }
  }

  const isSubmitting = clearTestDataMutation.isLoading || fullResetMutation.isLoading

  return (
    <div style={{ padding: "8px 0" }}>
      <Alert
        message="Diqqat: Ushbu bo'lim ma'lumotlar bazasini tozalash va tiklash uchun mo'ljallangan"
        description="Amallarni bajarishdan oldin ma'lumotlar soni va xavfsizlik shartlarini diqqat bilan o'rganing."
        type="warning"
        showIcon
        icon={<AlertTriangle size={20} />}
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* Summary Card */}
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space align="center">
              <Database size={18} color="#2563EB" />
              <span>{i18n.t("dbStatTitle")}</span>
            </Space>
            <Button
              size="small"
              icon={<RefreshCw size={13} style={{ marginRight: 4 }} />}
              onClick={() => refetch()}
            >
              Yangilash
            </Button>
          </div>
        }
        size="small"
        style={{ marginBottom: 20, borderRadius: 8 }}
      >
        {loadingSummary ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin size="small" />
          </div>
        ) : (
          <Descriptions size="small" bordered column={{ xl: 3, lg: 3, md: 2, sm: 2, xs: 1 }}>
            <Descriptions.Item label={i18n.t("dbStatSales")}>{summary?.salesCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatPurchases")}>{summary?.purchasesCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatReturnSales")}>{summary?.returnSalesCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatReturnPurchases")}>{summary?.returnPurchasesCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatCashOut")}>{summary?.moneyCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatCashIn")}>{summary?.moneyReceiptsCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatOrders")}>{summary?.ordersCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatShowcases")}>{summary?.showcasesCount ?? 0} ta</Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatProducts")}>
              <Tag color="blue">{summary?.productsCount ?? 0} ta</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatCounterparties")}>
              <Tag color="cyan">{summary?.counterpartiesCount ?? 0} ta</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatManagers")}>
              <Tag color="green">{summary?.managersCount ?? 0} ta</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatDeliveries")}>
              <Tag color="purple">{summary?.deliveriesCount ?? 0} ta</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatDrivers")}>
              <Tag color="blue">{summary?.driversCount ?? 0} ta</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t("dbStatVehicles")}>
              <Tag color="cyan">{summary?.vehiclesCount ?? 0} ta</Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* Action 1: Clear Test Data */}
      <Card
        size="small"
        style={{ marginBottom: 16, borderRadius: 8, borderColor: "#FED7AA", backgroundColor: "#FFFBF7" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Typography.Text strong style={{ fontSize: 14, color: "#C2410C" }}>
              🧹 Test tranzaksiyalarini tozalash (Clear Test Data)
            </Typography.Text>
            <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0 0", fontSize: 12 }}>
              Faqat savdolar, xaridlar, qaytuvlar, pul oqimlari va buyurtmalarni o'chiradi.
              Mahsulotlar, mijozlar va tizim sozlamalari <strong>{i18n.t("dbStatKept")}</strong>.
              <br />
              {i18n.t("dbBackupFirstNote")}
            </Typography.Paragraph>
          </div>
          <Button
            danger
            icon={<Trash2 size={14} style={{ marginRight: 4 }} />}
            onClick={() => {
              setModalType("clearTest")
              setConfirmInput("")
            }}
          >
            Tranzaksiyalarni Tozalash
          </Button>
        </div>
      </Card>

      {/* Action 2: Full Database Reset */}
      <Card
        size="small"
        style={{ borderRadius: 8, borderColor: "#FECACA", backgroundColor: "#FEF2F2" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Typography.Text strong style={{ fontSize: 14, color: "#DC2626" }}>
              ⚠️ Bazani to'liq dastlabki (zavod) holatiga qaytarish (Factory Reset)
            </Typography.Text>
            <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0 0", fontSize: 12 }}>
              Barcha ma'lumotlar (tovarlar, mijozlar, savdolar) butunlay o'chiriladi va baza yangi o'rnatilgan holatga qaytadi.
              <br />
              {i18n.t("dbBackupFirstNote")}
            </Typography.Paragraph>
          </div>
          <Button
            type="primary"
            danger
            icon={<RotateCcw size={14} style={{ marginRight: 4 }} />}
            onClick={() => {
              setModalType("fullReset")
              setConfirmInput("")
            }}
          >
            To'liq Boshlang'ich Holatga Qaytarish
          </Button>
        </div>
      </Card>

      {/* Safety Confirmation Modal */}
      <Modal
        title={
          <Space align="center">
            <AlertTriangle size={20} color="#DC2626" />
            <span style={{ color: "#DC2626", fontWeight: 700 }}>
              {modalType === "clearTest"
                ? "Test Tranzaksiyalarini Tozalashni Tasdiqlang"
                : "Bazani To'liq Qaytarishni Tasdiqlang!"}
            </span>
          </Space>
        }
        visible={modalType !== null}
        /*
          XATO TUZATILDI (guruh: "bazani tozalab bo'lmayapti, bosilyapti
          lekin hech narsa chiqmayapti"): bu tasdiqlash oynasi Sozlamalar
          oynasi ICHIDAN ochiladi; antd standart z-index 1000 bilan u
          Sozlamalar oynasi (1001) ORQASIDA qolib, ko'rinmas edi.
        */
        zIndex={1200}
        onCancel={() => {
          if (!isSubmitting) {
            setModalType(null)
            setAdminPassword("")
            setConfirmInput("")
          }
        }}
        footer={[
          <Button key="cancel" disabled={isSubmitting} onClick={() => setModalType(null)}>
            Bekor qilish
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={isSubmitting}
            disabled={!adminLogin.trim() || !adminPassword.trim() || confirmInput.trim().toUpperCase() !== "TOZALASH"}
            onClick={handleExecute}
          >
            Tasdiqlayman va Tozalayman
          </Button>,
        ]}
        width={520}
      >
        <Alert
          type="error"
          showIcon
          icon={<AlertTriangle size={16} />}
          message={
            modalType === "clearTest"
              ? "Ushbu amal barcha sinov savdolari, xaridlari va pul oqimlarini o'chirib tashlaydi. Ushbu amalni ortga qaytarib bo'lmaydi!"
              : "DIQQAT! Ushbu amal bazadagi BARCHA ma'lumotlarni o'chiradi. Ushbu amalni ortga qaytarib bo'lmaydi!"
          }
          style={{ marginBottom: 16, borderRadius: 6 }}
        />

        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <div>
            <Typography.Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
              Super-Admin Logini yoki Elektron pochtasi:
            </Typography.Text>
            <Input
              prefix={<User size={14} color="#94A3B8" />}
              placeholder={i18n.t("phSuperAdminLogin")}
              value={adminLogin}
              onChange={(e) => setAdminLogin(e.target.value)}
            />
          </div>

          <div>
            <Typography.Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
              Super-Admin Paroli:
            </Typography.Text>
            <Input.Password
              prefix={<Lock size={14} color="#94A3B8" />}
              placeholder={i18n.t("phSuperAdminPassword")}
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
          </div>

          <div>
            <Typography.Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
              Tasdiqlash uchun quyidagi maydonga <Tag color="error">TOZALASH</Tag> so'zini kiriting:
            </Typography.Text>
            <Input
              placeholder="TOZALASH"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
            />
          </div>
        </Space>
      </Modal>
    </div>
  )
}
